/**
 * Pending-camera records: how a transitory Iris placeholder is persisted in
 * state-sync and rebuilt from it.
 *
 * A bulk-added Iris camera that has not authenticated yet is stored on a bridge
 * as `control.studio.device["transitory:<host>:<port>"]` holding only `name`,
 * `added_at` and `config_health = AUTH_PENDING`. It has no `control.device`
 * entry, model or URI, so no bridge builds a driver for it, older Studio builds
 * ignore it (their restore and tile paths need a model and a URI) and it never
 * counts toward the plan's source limit (those counts walk `control.device`).
 * The key carries the endpoint the Iris link needs after a restart, and keeps
 * the record recognisable if an older Studio re-saves it without config_health.
 *
 * Per rule: wire-contract-changes-must-not-break-old-firmware (ai.knowledge/global)
 * — only fields every peer already tolerates, plus the new optional enum.
 */
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import { StudioDeviceConfigHealth } from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { StudioDevice } from '@tryiris-ai/common.protojs/studio/v1/studio';
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { Protocol } from '@studio/models/video';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Device } from '#~/device/index.js';
import type { Discovery } from '#~/device/discovery/index.js';
import { buildPlaceholderVideo, type TransitoryPlaceholder } from './transitory.js';

/** Prefix of placeholder ids and of the keys their records are stored under. */
export const TRANSITORY_ID_PREFIX = 'transitory:';

/** The fields of a pending-camera record that Studio reads or writes. */
export type PendingAuthRecord = Pick<
  DeepPartial<StudioDevice>,
  'name' | 'addedAt' | 'configHealth'
>;

/** A bridge's control tree, as live state or as a saved snapshot. */
type ControlTree = T_Maybe<{
  readonly device?: T_Maybe<Readonly<Record<string, unknown>>>;
  readonly studio?: T_Maybe<{
    readonly device?: T_Maybe<Readonly<Record<string, T_Maybe<Readonly<PendingAuthRecord>>>>>;
  }>;
}>;

/**
 * Whether a transitory record with this config_health is still pending.
 * Exhaustive over the enum, so a new value fails the build until mapped here.
 */
export const keepsRecordPending = (health: T_Maybe<StudioDeviceConfigHealth>): boolean => {
  switch (health) {
    case StudioDeviceConfigHealth.STUDIO_DEVICE_CONFIG_HEALTH_AUTH_PENDING:
      return true;
    // Absent: an older Studio re-saved the record without the field; the key still marks it.
    case undefined:
    case StudioDeviceConfigHealth.STUDIO_DEVICE_CONFIG_HEALTH_UNSPECIFIED:
      return true;
    case StudioDeviceConfigHealth.STUDIO_DEVICE_CONFIG_HEALTH_OK:
    case StudioDeviceConfigHealth.STUDIO_DEVICE_CONFIG_HEALTH_VIDEO_MISSING:
    case StudioDeviceConfigHealth.STUDIO_DEVICE_CONFIG_HEALTH_CONTROL_MISSING:
    case StudioDeviceConfigHealth.STUDIO_DEVICE_CONFIG_HEALTH_MODEL_GENERIC:
    case StudioDeviceConfigHealth.STUDIO_DEVICE_CONFIG_HEALTH_MULTIPLE_MISSING:
      return false;
    default: {
      const unmapped: never = health;
      void unmapped;
      // A value from a newer writer: keep showing the camera rather than hide it.
      // Per rule: absent-data-gates-permissively (ai.knowledge/global).
      return true;
    }
  }
};

/** Whether `control.studio.device[key]` is a pending-camera record. */
export const isPendingAuthRecord = (
  key: string,
  record: T_Maybe<Readonly<PendingAuthRecord>>,
  hasOperationalDevice: boolean
): boolean =>
  key.startsWith(TRANSITORY_ID_PREFIX) &&
  !!record &&
  !hasOperationalDevice &&
  keepsRecordPending(record.configHealth);

/** The record a pending camera is persisted as (seconds, like `added_at` everywhere). */
export const toPendingAuthRecord = (
  placeholder: Pick<TransitoryPlaceholder, 'name' | 'createdAt'>
): PendingAuthRecord => ({
  name: placeholder.name,
  addedAt: placeholder.createdAt / 1000,
  configHealth: StudioDeviceConfigHealth.STUDIO_DEVICE_CONFIG_HEALTH_AUTH_PENDING
});

/** The endpoint encoded in a transitory key (`transitory:<host>:<port>`). */
export const parseTransitoryKey = (
  key: string
): T_Maybe<{ discoveryId: Discovery['id']; host: string; port?: number }> => {
  if (!key.startsWith(TRANSITORY_ID_PREFIX)) {
    return undefined;
  }

  const discoveryId = key.slice(TRANSITORY_ID_PREFIX.length);
  const separator = discoveryId.lastIndexOf(':');
  const host = separator > 0 ? discoveryId.slice(0, separator) : discoveryId;
  const port = separator > 0 ? Number(discoveryId.slice(separator + 1)) : Number.NaN;

  if (!host) {
    return undefined;
  }

  return {
    discoveryId: discoveryId as unknown as Discovery['id'],
    host,
    ...(Number.isInteger(port) && port > 0 ? { port } : {})
  };
};

/**
 * The Iris discovery a pending record stands for: the same row
 * `buildIrisDiscoveries` makes for a camera seen only through Electron mDNS,
 * which is all the Iris link needs (address + port).
 */
const toIrisDiscovery = (
  discoveryId: Discovery['id'],
  host: string,
  port: T_Maybe<number>,
  name: string
): Discovery =>
  ({
    id: discoveryId,
    address: host,
    ...(port !== undefined ? { port } : {}),
    agentId: host,
    name,
    irisEnabled: true,
    video: { protocol: Protocol.IRIS, host, ...(port !== undefined ? { port } : {}), name }
  }) as unknown as Discovery;

/** The placeholder a pending record renders as, hosted on `hostAgentId` in `roomId`. */
export const placeholderFromPendingRecord = (
  key: string,
  record: Readonly<PendingAuthRecord>,
  hostAgentId: Agent['id'],
  roomId: Room['id']
): T_Maybe<TransitoryPlaceholder> => {
  const endpoint = parseTransitoryKey(key);

  if (!endpoint) {
    return undefined;
  }

  const name = record.name || endpoint.host;

  return {
    id: key as unknown as Device['id'],
    name,
    transitoryKind: 'iris',
    discoveryId: endpoint.discoveryId,
    originalDiscovery: toIrisDiscovery(endpoint.discoveryId, endpoint.host, endpoint.port, name),
    address: endpoint.host,
    ...(endpoint.port !== undefined ? { port: endpoint.port } : {}),
    video: buildPlaceholderVideo(endpoint.host),
    createdAt: (record.addedAt ?? 0) * 1000,
    hostAgentId,
    roomId
  };
};

/** Every pending-camera record in a bridge's control tree, keyed by device id. */
const pendingAuthEntries = (control: ControlTree): [string, Readonly<PendingAuthRecord>][] =>
  Object.entries(control?.studio?.device ?? {}).filter(
    (entry): entry is [string, Readonly<PendingAuthRecord>] =>
      isPendingAuthRecord(entry[0], entry[1], control?.device?.[entry[0]] !== undefined)
  );

/** The placeholders for the pending records in one bridge's control tree. */
export const pendingAuthPlaceholders = (
  hostAgentId: Agent['id'],
  roomId: Room['id'],
  control: ControlTree
): Record<Device['id'], TransitoryPlaceholder> => {
  const placeholders: Record<Device['id'], TransitoryPlaceholder> = {};

  for (const [key, record] of pendingAuthEntries(control)) {
    const placeholder = placeholderFromPendingRecord(key, record, hostAgentId, roomId);

    if (placeholder) {
      placeholders[placeholder.id] = placeholder;
    }
  }

  return placeholders;
};

/**
 * The pending records of a saved snapshot, ready to write back to a bridge
 * that lost them. config_health is re-stamped in case an older Studio dropped it.
 */
export const pendingAuthReplayRecords = (control: ControlTree): Record<string, PendingAuthRecord> =>
  Object.fromEntries(
    pendingAuthEntries(control).map(([key, record]) => [
      key,
      {
        ...(record.name !== undefined ? { name: record.name } : {}),
        ...(record.addedAt !== undefined ? { addedAt: record.addedAt } : {}),
        configHealth: StudioDeviceConfigHealth.STUDIO_DEVICE_CONFIG_HEALTH_AUTH_PENDING
      }
    ])
  );
